/**
 * The storage envelope (architecture §4.1): `{ format: 'marg-save', schema, savedAt, checksum, data }` where
 * `checksum` = FNV-1a hex of `JSON.stringify(data)`. Envelope schema migrations live in `./migrations.ts`.
 */
import { fnv1a, hex32, type SaveEnvelope, type SaveState } from '../../shared';
import { CURRENT_SCHEMA, migrateEnvelope, type EnvelopeMigrations } from './migrations';

/** FNV-1a hex of `JSON.stringify(data)` (architecture §4.1). */
export function checksumOf(data: unknown): string {
  return hex32(fnv1a(JSON.stringify(data)));
}

/** Wraps a save state into a storable envelope. */
export function wrapEnvelope(data: SaveState, savedAt: number): SaveEnvelope {
  return { format: 'marg-save', schema: CURRENT_SCHEMA, savedAt, checksum: checksumOf(data), data };
}

export type EnvelopeParse =
  | { ok: true; envelope: SaveEnvelope; migratedFrom: number | null }
  | { ok: false; error: string };

/**
 * Raw string → validated, migrated envelope (load pipeline step 1, architecture §4.2): JSON.parse → format and checksum
 * check → envelope migrations to {@link CURRENT_SCHEMA}. Never throws.
 */
export function parseEnvelope(raw: string, migrations?: EnvelopeMigrations): EnvelopeParse {
  let obj: unknown;
  try { obj = JSON.parse(raw); } catch (e) { return { ok: false, error: `json: ${(e as Error).message}` }; }
  if (typeof obj !== 'object' || obj === null) return { ok: false, error: 'envelope: not an object' };
  const env = obj as Partial<SaveEnvelope>;
  if (env.format !== 'marg-save') return { ok: false, error: `envelope: bad format ${String(env.format)}` };
  if (typeof env.schema !== 'number' || !Number.isInteger(env.schema)) return { ok: false, error: 'envelope: missing schema' };
  if (typeof env.data !== 'object' || env.data === null) return { ok: false, error: 'envelope: missing data' };
  if (typeof env.checksum !== 'string' || env.checksum !== checksumOf(env.data)) return { ok: false, error: 'envelope: checksum mismatch' };
  if (env.schema > CURRENT_SCHEMA) return { ok: false, error: `envelope: schema ${env.schema} is newer than ${CURRENT_SCHEMA}` };
  try {
    const from = env.schema;
    const migrated = migrateEnvelope(env as SaveEnvelope, migrations);
    return { ok: true, envelope: migrated, migratedFrom: from === CURRENT_SCHEMA ? null : from };
  } catch (e) {
    return { ok: false, error: `envelope migration: ${(e as Error).message}` };
  }
}
